import { useState, useEffect } from 'react';
import { api } from '../api';

interface WTDTabProps {
  driverId: number | null;
}

export function WTDTab({ driverId }: WTDTabProps) {
  const [data, setData] = useState<any>(null);
  const [weeks, setWeeks] = useState(17);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!driverId) return;
    setLoading(true);
    api.getWTDAnalysis(driverId, weeks)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [driverId, weeks]);

  if (!driverId) return <div className="text-center py-8 text-gray-400 text-sm">Select a driver to view WTD analysis</div>;
  if (loading) return <div className="text-center py-8 text-gray-400 text-sm">Analysing Working Time Directive compliance...</div>;
  if (!data) return <div className="text-center py-8 text-gray-400 text-sm">No WTD data available</div>;

  const s = data.summary;

  return (
    <div className="space-y-5">
      {/* Reference period selector + status */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500">Reference period:</span>
          <button
            onClick={() => setWeeks(17)}
            className={`px-2.5 py-1 text-xs rounded border ${weeks === 17 ? 'bg-slate-700 text-white border-slate-700' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
          >17 weeks</button>
          <button
            onClick={() => setWeeks(26)}
            className={`px-2.5 py-1 text-xs rounded border ${weeks === 26 ? 'bg-slate-700 text-white border-slate-700' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
          >26 weeks</button>
        </div>
        {s && (
          <span className={`px-2.5 py-1 text-xs font-semibold rounded ${s.compliant ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
            {s.compliant ? 'WTD COMPLIANT' : 'WTD NON-COMPLIANT'}
          </span>
        )}
      </div>

      {/* Summary table */}
      {s && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">WTD Summary</h3>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-gray-100">
              <tr>
                <td className="py-1.5 text-gray-500">Average Weekly Working Hours</td>
                <td className={`py-1.5 text-right font-medium ${s.average_weekly_hours > 48 ? 'text-red-600' : 'text-gray-900'}`}>
                  {s.average_weekly_hours}h
                  <span className="text-xs text-gray-400 ml-1.5">/ 48h limit</span>
                </td>
              </tr>
              <tr>
                <td className="py-1.5 text-gray-500">Total Hours in Period</td>
                <td className="py-1.5 text-right font-medium text-gray-900">{s.total_hours_in_period}h</td>
              </tr>
              <tr>
                <td className="py-1.5 text-gray-500">Weeks Analysed</td>
                <td className="py-1.5 text-right font-medium text-gray-900">{s.weeks_analysed}</td>
              </tr>
              <tr>
                <td className="py-1.5 text-gray-500">Weeks Over 48h</td>
                <td className={`py-1.5 text-right font-medium ${s.weeks_over_48h > 0 ? 'text-amber-600' : 'text-gray-900'}`}>{s.weeks_over_48h}</td>
              </tr>
              <tr>
                <td className="py-1.5 text-gray-500">Weeks Over 60h (absolute max)</td>
                <td className={`py-1.5 text-right font-medium ${s.weeks_over_60h > 0 ? 'text-red-600' : 'text-gray-900'}`}>{s.weeks_over_60h}</td>
              </tr>
              <tr>
                <td className="py-1.5 text-gray-500">Infringements Detected</td>
                <td className={`py-1.5 text-right font-medium ${s.infringement_count > 0 ? 'text-red-600' : 'text-gray-900'}`}>{s.infringement_count}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Infringements */}
      {data.infringements?.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            WTD Infringements ({data.infringements.length})
          </h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-1.5 text-xs font-semibold text-gray-500">Date</th>
                <th className="text-left py-1.5 text-xs font-semibold text-gray-500">Type</th>
                <th className="text-left py-1.5 text-xs font-semibold text-gray-500">Description</th>
                <th className="text-center py-1.5 text-xs font-semibold text-gray-500">Severity</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.infringements.map((inf: any, i: number) => (
                <tr key={i} className={inf.severity === 'major' ? 'bg-red-50' : ''}>
                  <td className="py-2 text-gray-700 font-medium whitespace-nowrap">{inf.date || inf.week || '—'}</td>
                  <td className="py-2 text-gray-500 whitespace-nowrap">{inf.type.replace(/_/g, ' ')}</td>
                  <td className="py-2 text-gray-700">{inf.description}</td>
                  <td className="py-2 text-center">
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                      inf.severity === 'major' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                    }`}>{inf.severity.toUpperCase()}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Weekly breakdown table */}
      {data.weekly_breakdown?.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Weekly Breakdown</h3>
          <div className="border border-gray-200 rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Week</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500">WTD Hours</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500">Driving</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500">Other Work</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500">Days</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.weekly_breakdown.map((w: any) => {
                  const over60 = w.total_wtd_hours > 60;
                  const over48 = w.total_wtd_hours > 48;
                  return (
                    <tr key={w.week} className={`border-b border-gray-100 ${over60 ? 'bg-red-50' : over48 ? 'bg-amber-50' : ''}`}>
                      <td className="px-3 py-2 font-medium text-gray-700">{w.week}</td>
                      <td className={`px-3 py-2 text-right font-semibold ${over60 ? 'text-red-600' : over48 ? 'text-amber-600' : 'text-gray-900'}`}>
                        {w.total_wtd_hours}h
                      </td>
                      <td className="px-3 py-2 text-right text-gray-500">{w.total_driving_hours}h</td>
                      <td className="px-3 py-2 text-right text-gray-500">{w.total_work_hours}h</td>
                      <td className="px-3 py-2 text-right text-gray-500">{w.days_worked}</td>
                      <td className="px-3 py-2 text-center">
                        {over60 ? (
                          <span className="text-[10px] font-semibold bg-red-100 text-red-700 px-1.5 py-0.5 rounded">OVER 60h</span>
                        ) : over48 ? (
                          <span className="text-[10px] font-semibold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">OVER 48h</span>
                        ) : (
                          <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Info note if all weeks are zero */}
      {s && s.compliant && s.total_hours_in_period === 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded p-3">
          <p className="text-xs text-blue-700">
            No driving or working time recorded within the last {weeks} weeks. The driver is compliant as there is no working time to average.
            Card data may be older than the current reference period.
          </p>
        </div>
      )}

      {/* Regulatory note */}
      <div className="border-t border-gray-200 pt-3">
        <p className="text-[11px] text-gray-400 leading-relaxed">
          <span className="font-semibold text-gray-500">Working Time Directive</span> — Working time = driving + other work (excludes rest and availability).
          Average working week must not exceed 48h over the {weeks}-week reference period.
          No single week may exceed 60h. A 30-minute break is required after 6 consecutive hours of work.
          Night workers must not exceed 10h in any 24-hour period.
          Weeks with no recorded activity count as 0h in the average.
        </p>
      </div>
    </div>
  );
}
